import {
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { AssetService } from '../../services/asset.service';
import { BookingService, CheckoutAmountQuote } from '../../services/booking.service';
import { savePendingBookingSelection } from '../../shared/booking/booking-selection-persistence';
import {
  Asset,
  bookingDurationHours,
  hourlyRateForDate,
  quoteRentalSubtotal,
} from '../../models/asset.model';
import {
  RESERVATION_CONDITIONS_VERSION,
} from '../../models/booking.model';

interface CheckoutDraft {
  spaceId: string;
  userId: string;
  date: string;
  startTime: string;
  endTime: string;
  totalPrice: number;
  ownerId: string;
}

@Component({
  selector: 'app-booking-payment',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './booking-payment.component.html',
})
export class BookingPaymentComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(Auth);
  private bookingService = inject(BookingService);
  private assetService = inject(AssetService);

  readonly draft = signal<CheckoutDraft | null>(null);
  readonly space = signal<Asset | null>(null);
  readonly checkoutMode = signal<'MANUAL' | 'STRIPE'>('MANUAL');
  readonly checkoutQuote = signal<CheckoutAmountQuote | null>(null);
  readonly loading = signal(true);
  readonly submitting = signal(false);
  readonly payError = signal<string | null>(null);
  readonly checkoutNotice = signal<string | null>(null);
  readonly conditionsAccepted = signal(false);

  readonly totalPrice = computed<number>(() => this.draft()?.totalPrice ?? 0);
  readonly isStripeCheckout = computed(() => this.checkoutMode() === 'STRIPE');

  readonly platformFee = computed<number>(() => {
    const q = this.checkoutQuote();
    return q ? this.centsToEuros(q.applicationFeeCents) : 0;
  });

  readonly pendingBalance = computed<number>(() => {
    const q = this.checkoutQuote();
    return q ? this.centsToEuros(q.pendingBalanceCents) : 0;
  });

  readonly payNowAmount = computed<number>(() => {
    const q = this.checkoutQuote();
    return q ? this.centsToEuros(q.payNowCents) : 0;
  });

  readonly durationHours = computed<number>(() => {
    const d = this.draft();
    if (!d?.startTime || !d?.endTime) return 0;
    return bookingDurationHours(d.startTime, d.endTime);
  });

  readonly venueSubtotal = computed<number>(() => {
    const b = this.draft();
    const s = this.space();
    if (!b || !s || !b.date || !b.startTime || !b.endTime) return 0;
    const hours = bookingDurationHours(b.startTime, b.endTime);
    const rate = hourlyRateForDate(s, b.date);
    return this.round2(quoteRentalSubtotal(s, rate, hours));
  });

  readonly cleaningConcept = computed<number>(() => {
    const total = this.totalPrice();
    const venue = this.venueSubtotal();
    const diff = this.round2(total - venue);
    return diff > 0 ? diff : 0;
  });

  /** Email sent to Stripe as customer_email (locked on their hosted page). */
  readonly checkoutEmail = computed(() => this.auth.currentUser?.email?.trim() ?? '');

  readonly canConfirmPayment = computed(() =>
    this.conditionsAccepted()
    && !this.submitting()
    && !!this.checkoutEmail()
    && !!this.checkoutQuote(),
  );

  private round2(n: number): number {
    return Math.round(n * 100) / 100;
  }

  private centsToEuros(cents: number): number {
    return this.round2(cents / 100);
  }

  ngOnInit(): void {
    const query = this.route.snapshot.queryParamMap;
    if (query.get('cancelled') === '1') {
      this.checkoutNotice.set(
        'Has cancelado el pago en Stripe. Puedes intentarlo de nuevo cuando quieras.',
      );
    }

    const spaceId = this.route.snapshot.paramMap.get('spaceId') ?? query.get('spaceId') ?? '';
    const totalPrice = Number(query.get('totalPrice') ?? NaN);
    const draft: CheckoutDraft = {
      spaceId,
      userId: query.get('userId') ?? '',
      date: query.get('date') ?? '',
      startTime: query.get('startTime') ?? '',
      endTime: query.get('endTime') ?? '',
      totalPrice,
      ownerId: query.get('ownerId') ?? '',
    };

    if (
      !draft.spaceId || !draft.userId || !draft.date || !draft.startTime || !draft.endTime
      || !draft.ownerId || !Number.isFinite(totalPrice) || totalPrice <= 0
    ) {
      this.router.navigate(['/explorar']);
      return;
    }

    this.draft.set(draft);
    void Promise.all([
      this.assetService.getSpaceById(draft.spaceId),
      this.loadOwnerCheckoutMode(draft.ownerId, draft.totalPrice),
    ])
      .then(([space]) => {
        if (!space) {
          this.router.navigate(['/explorar']);
          return;
        }
        this.space.set(space);
      })
      .finally(() => this.loading.set(false));
  }

  private async loadOwnerCheckoutMode(ownerId: string, totalPrice: number): Promise<void> {
    try {
      const data = await this.bookingService.getOwnerCheckoutMode(ownerId, totalPrice);
      this.checkoutMode.set(data.checkoutMode);
      this.checkoutQuote.set(data.quote ?? null);
      if (!data.quote) {
        this.payError.set('No se pudo calcular el importe del checkout. Inténtalo de nuevo.');
      }
    } catch {
      this.checkoutMode.set('MANUAL');
      this.checkoutQuote.set(null);
      this.payError.set('No se pudo cargar el modo de pago del espacio. Inténtalo de nuevo.');
    }
  }

  async redirectToCheckout(): Promise<void> {
    if (!this.canConfirmPayment()) return;

    const draft = this.draft();
    const uid = this.auth.currentUser?.uid ?? '';
    if (!draft || !uid || uid !== draft.userId) {
      this.payError.set('Sesión inválida. Vuelve a iniciar la reserva.');
      return;
    }
    if (!this.checkoutEmail()) {
      this.payError.set('Tu cuenta debe tener un email verificado para pagar con Stripe.');
      return;
    }

    this.submitting.set(true);
    this.payError.set(null);

    try {
      const { url } = await this.bookingService.createCheckoutSession({
        ...draft,
        returnOrigin: window.location.origin,
        reservationConditionsAccepted: true,
        conditionsVersion: RESERVATION_CONDITIONS_VERSION,
        userAgent: navigator.userAgent,
      });
      window.location.assign(url);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message
        ?? 'No se pudo redirigir al Checkout. Inténtalo de nuevo.';
      this.payError.set(msg);
    } finally {
      this.submitting.set(false);
    }
  }

  goBack(): void {
    const draft = this.draft();
    const spaceId = draft?.spaceId?.trim();
    if (spaceId && draft) {
      savePendingBookingSelection({
        spaceId,
        date: draft.date,
        startTime: draft.startTime,
        endTime: draft.endTime,
        hasCleaning: this.cleaningConcept() > 0,
      });
      void this.router.navigate(['/espacio', spaceId, 'reservar']);
      return;
    }
    void this.router.navigate(['/explorar']);
  }

  formatDate(iso: string | undefined): string {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    const date = new Date(y, m - 1, d);
    const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
    ];
    return `${days[date.getDay()]} ${d} ${months[m - 1]} ${y}`;
  }
}
